<template>
  <div class="chart-grid chart-grid-4">
    <div class="chart-card">
      <h3 class="card-title">Overall Status</h3>
      <div v-if="!hasStatusData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="donut"
          height="300"
          :options="statusChartOptions"
          :series="statusSeries"
        />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Status by Team</h3>
      <div v-if="!hasTeamData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="bar"
          height="300"
          :options="departmentChartOptions"
          :series="departmentSeries"
        />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Status by Location</h3>
      <div v-if="!hasLocationData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="bar"
          height="300"
          :options="locationChartOptions"
          :series="locationSeries"
        />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Overdue Aging</h3>
      <div v-if="!hasAgingData" class="no-data">No overdue tasks</div>
      <div v-else class="chart-body">
        <apexchart
          type="bar"
          height="300"
          :options="agingChartOptions"
          :series="agingSeries"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { CR_STATUS_ORDER, rollupLocations, stackedStatusSeries } from "../../utils/dashboardChartData";
import { agingBarOptions, donutOptions, stackedBarOptions } from "../../utils/dashboardTheme";

export default defineComponent({
  name: "ComplianceReviewSection",
  props: {
    crTasks: { type: Array, default: () => [] },
    teamTasks: { type: Array, default: () => [] },
    overdueAging: { type: Array, default: () => [] },
  },
  data(): any {
    return {};
  },
  computed: {
    statusSeries(): number[] {
      return ((this.crTasks as any[]) || []).map((item: any) => item.total || 0);
    },
    hasStatusData(): boolean {
      return this.statusSeries.some((value: number) => Number(value) > 0);
    },
    statusKey(): string {
      return this.statusSeries.join("-");
    },
    statusChartOptions(): Record<string, any> {
      const labels = ((this.crTasks as any[]) || []).map((item: any) => item.label || "N/A");
      return donutOptions(labels);
    },
    departmentsFlat(): any[] {
      return ((this.teamTasks as any[]) || [])
        .flatMap((location: any) => location.teams || [])
        .filter((team: any) => (Number(team.totalTasks) || 0) > 0);
    },
    hasTeamData(): boolean {
      return this.departmentsFlat.length > 0;
    },
    departmentSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.departmentsFlat, CR_STATUS_ORDER);
    },
    teamKey(): string {
      return this.departmentsFlat.map((team: any) => team.teamName).join("-");
    },
    departmentChartOptions(): Record<string, any> {
      const teams: any[] = this.departmentsFlat;
      return stackedBarOptions(
        teams.map((team: any) => team.teamName),
        this.departmentSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    locationRows(): any[] {
      return rollupLocations(this.teamTasks as any[], CR_STATUS_ORDER);
    },
    hasLocationData(): boolean {
      return this.locationRows.length > 0;
    },
    locationKey(): string {
      return this.locationRows.map((location: any) => location.locationName).join("-");
    },
    locationSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.locationRows, CR_STATUS_ORDER);
    },
    locationChartOptions(): Record<string, any> {
      return stackedBarOptions(
        this.locationRows.map((location: any) => location.locationName),
        this.locationSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    agingItems(): any[] {
      return ((this.overdueAging as any[]) || []);
    },
    hasAgingData(): boolean {
      return this.agingItems.some((item: any) => Number(item.total) > 0);
    },
    agingKey(): string {
      return this.agingItems.map((item: any) => item.total).join("-");
    },
    agingSeries(): { name: string; data: number[] }[] {
      const items: any[] = this.agingItems;
      return [
        {
          name: "Overdue Tasks",
          data: items.map((item: any) => item.total || 0),
        },
      ];
    },
    agingChartOptions(): Record<string, any> {
      return agingBarOptions(this.agingItems.map((item: any) => item.label));
    },
  },
});
</script>
